import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  ArrowLeft, ExternalLink, Target, Brain, Users, Heart,
  Sparkles, Crown, Calendar, MessageCircle, CheckSquare,
  Globe, Shield, Eye, Star, Zap, Home, Wrench, GraduationCap,
  FileText, TrendingUp, Compass, Coffee, Gift, TreePine
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import PersistentFooter from './PersistentFooter';
import UniversalHeartToggle from './UniversalHeartToggle';

const ToolFedAppsPage = ({ onNavigateHome, onNavigateToGame }) => {
  const [spiralPhase, setSpiralPhase] = useState(0);
  const [activeCategory, setActiveCategory] = useState('consciousness_tools');

  // Grant spiral animation
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.2) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  const toolCategories = {
    consciousness_tools: {
      title: "Consciousness Tools",
      icon: Brain,
      color: "purple",
      tools: [
        { name: "Business Model Canvas 2.0", description: "Consciousness-driven business modeling", icon: Target, url: null },
        { name: "Vision Creation Worksheet", description: "Beyond Entrepreneurship 2.0 methodology", icon: Star, url: null },
        { name: "Brand-Name Builder", description: "Authentic brand identity creation", icon: Heart, url: null },
        { name: "8 Compass Point Phases", description: "WisconSing project completion methodology", icon: Compass, url: null }
      ]
    },
    partner_apps: {
      title: "Partner Apps & Platforms",
      icon: Globe,
      color: "cyan",
      tools: [
        { name: "Gratitude Living Deck", description: "Consciousness expansion card platform", icon: Heart, url: "https://living-deck.emergent.host" },
        { name: "Seedy Peeps", description: "Artist collaboration hub", icon: Users, url: "https://artist-hub-21.emergent.host" },
        { name: "TrustWoven", description: "Trust-based collaboration network", icon: Shield, url: "https://trustwoven.com" },
        { name: "PlanetaryFed", description: "Regenerative civilization coordination", icon: Globe, url: "https://planetaryfed.com" }
      ]
    },
    venture_tools: {
      title: "Venture & Investment",
      icon: TrendingUp,
      color: "green", 
      tools: [
        { name: "RegenVen", description: "Regenerative venture platform", icon: TrendingUp, url: "https://regenven.com" },
        { name: "Conscious Stock", description: "Spiritual media investment collaboration", icon: Star, url: "https://spiritual-media.emergent.host" },
        { name: "TrustFlo", description: "Private Trusts Resources", icon: Shield, url: "https://trustflo-legal.emergent.host" },
        { name: "WisconSing", description: "Wisdom gathering retreat management", icon: TreePine, url: "https://wisdomgather.emergent.host" },
        { name: "Defiance Springs", description: "Regenerative retreat sanctuary", icon: Coffee, url: "https://wisdomgather.emergent.host" },
        { name: "Bluff Bashers", description: "Community collaboration mechanics", icon: Zap, url: "https://kid-mechanic.emergent.host" }
      ]
    },
    community_platforms: {
      title: "Community Platforms",
      icon: Users,
      color: "blue",
      tools: [
        { name: "Bluff Bashers", description: "Community collaboration mechanics", icon: Zap, url: "https://kid-mechanic.emergent.host" },
        { name: "Remedy Council Network", description: "Distributed conflict resolution", icon: Crown, url: null },
        { name: "Mastery Exchange Hub", description: "Skills and wisdom sharing platform", icon: GraduationCap, url: null },
        { name: "Mycology Guild", description: "Fungi wisdom and regenerative practices", icon: TreePine, url: null }
      ]
    }
  };

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_mairy-chat/artifacts/j2ey1004_16677AAA-CE81-41C3-B6C8-01247CBE5D2A.jpeg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center'
      }}
    >
      {/* Grant spiral background */}
      <div className="fixed inset-0 pointer-events-none opacity-15" style={{ zIndex: 1 }}>
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(10) rotate(${spiralPhase}deg)`,
            filter: 'blur(4px) saturate(1.6) brightness(1.3)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantitudeSpiral size={400} />
        </div>
      </div>

      {/* Cosmic overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/20 to-black/60"></div>

      <div className="relative z-10 max-w-7xl mx-auto p-6">
        
        {/* Hero Header */}
        <div className="text-center pt-8 mb-12">
          <div className="flex items-center justify-between mb-8">
            <Button
              onClick={onNavigateToGame}
              variant="outline"
              className="text-white border-white/40 hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Game
            </Button>
            
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => window.open('https://planetaryfed.com', '_blank')}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
              >
                <Globe className="w-4 h-4 mr-2" />
                PlanetaryFed.com
              </Button>
              
              <Button
                onClick={onNavigateToGame}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Enter the Game
              </Button>
            </div>
          </div>
          
          <div className="bg-black/60 backdrop-blur-xl rounded-3xl p-12 border border-cyan-400/30 shadow-2xl">
            <h1 
              className="text-6xl font-bold text-white mb-6"
              style={{ 
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 30px rgba(255, 255, 255, 0.8)'
              }}
            >
              ToolFed Apps & Partners
            </h1>
            
            <p 
              className="text-2xl text-cyan-200 mb-8"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              Revolutionary Consciousness-Driven Business & Collaboration Tools
            </p>
            
            <div className="max-w-4xl mx-auto">
              <p 
                className="text-xl text-white/90 leading-relaxed mb-8"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Transform every business challenge into consciousness expansion. 
                Build ventures that serve individual growth AND collective awakening.
              </p>
            </div>
          </div>
        </div>

        {/* Category Navigation */}
        <div className="flex justify-center mb-8">
          <div className="bg-black/40 backdrop-blur-xl rounded-2xl p-2 border border-white/20">
            {Object.entries(toolCategories).map(([key, category]) => (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`px-6 py-3 rounded-xl transition-all ${
                  activeCategory === key 
                    ? `bg-${category.color}-500/30 text-${category.color}-200 border border-${category.color}-400/50` 
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                <category.icon className="w-4 h-4 mr-2" />
                {category.title}
              </button>
            ))}
          </div>
        </div>

        {/* Dynamic Tool Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {toolCategories[activeCategory].tools.map((tool, index) => (
            <Card key={index} className="bg-black/40 backdrop-blur-xl border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <CardContent className="p-6 text-center">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r from-${toolCategories[activeCategory].color}-600 to-${toolCategories[activeCategory].color}-400 flex items-center justify-center shadow-lg`}>
                  <tool.icon className="w-8 h-8 text-white" />
                </div>
                
                <h3 
                  className="text-white text-lg font-bold mb-3"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  {tool.name}
                </h3>
                
                <p 
                  className="text-white/80 text-sm leading-relaxed mb-4"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  {tool.description}
                </p>

                <Button
                  onClick={() => {
                    if (tool.url) {
                      window.open(tool.url, '_blank');
                    } else {
                      alert(`${tool.name} - Revolutionary consciousness-driven tool coming soon...`);
                    }
                  }}
                  variant="outline"
                  className={`text-${toolCategories[activeCategory].color}-300 border-${toolCategories[activeCategory].color}-400/50 ${
                    tool.url ? 'hover:bg-green-500/20 border-green-400/50' : ''
                  }`}
                  size="sm"
                >
                  {tool.url ? (
                    <>
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Visit Live
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-3 h-3 mr-1" />
                      Coming Soon
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Epic Vision Statement */}
        <div className="bg-gradient-to-br from-purple-900/60 to-cyan-900/60 backdrop-blur-xl rounded-3xl p-12 border border-white/20 shadow-2xl">
          <div className="text-center space-y-6">
            <h2 
              className="text-4xl font-bold text-white mb-6"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              Building the Ultimate Consciousness-Driven Ecosystem
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <Target className="w-12 h-12 mx-auto mb-4 text-purple-400" />
                <h3 className="text-white text-xl font-bold mb-3">Individual Growth</h3>
                <p className="text-white/80">
                  Every tool serves personal consciousness expansion while achieving practical goals
                </p>
              </div>
              
              <div className="text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-cyan-400" />
                <h3 className="text-white text-xl font-bold mb-3">Collective Wisdom</h3>
                <p className="text-white/80">
                  Collaborative tools that build trust, authenticity, and regenerative relationships
                </p>
              </div>
              
              <div className="text-center">
                <Heart className="w-12 h-12 mx-auto mb-4 text-pink-400" />
                <h3 className="text-white text-xl font-bold mb-3">Reality Creation</h3>
                <p className="text-white/80">
                  Transform challenges into consciousness expansion adventures that get real things done
                </p>
              </div>
            </div>
            
            <div className="mt-8 p-6 bg-black/40 rounded-2xl border border-white/10">
              <p 
                className="text-white/90 text-lg leading-relaxed italic"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                "When we belong to each other, fear dissolves. When fear dissolves, solutions always emerge - 
                and we create the reality we want to live in."
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <UniversalHeartToggle />
      <PersistentFooter />
    </div>
  );
};

export default ToolFedAppsPage;